// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CustomProductBadge, { CustomProductBadgeStyle } from "@awc/App/Components/CustomBadges/CustomProductBadge";
import ProductBuyButton from "@awc/App/Components/Product/ProductBuyButton";
import ProductGridContainer, { ProductGridContainerStyle } from "@awc/App/Components/Product/ProductGridContainer";
import ProductLeadTime from "@awc/App/Components/Product/ProductLeadTime";
import ProductQuoteButton from "@awc/App/Components/Product/ProductQuoteButton";
import xUseOverlayBadge from "@awc/App/Hooks/xUseOverlayBadge";
import { mergeWithCss, sharedBadgeStyle } from "@awc/XDL/Utilities";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductAttributes, { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    // XNG-Change
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    /**
     * XNG-Changes
     */
    showAttributes?: boolean;
    showAvailability?: boolean;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    /**
     * XNG-Changes
     */
    productGridContainerStyle?: ProductGridContainerStyle;
    attributes?: ProductAttributesStyles;
    badgeContainer?: InjectableCss;
    customProductBadge?: CustomProductBadgeStyle;
    addToList?: InjectableCss;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandWrapper: {
        css: css`
            margin: 6px 0;
            min-height: 22px;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 13 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 6px;
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        gap: 10,
        css: css`
            flex-grow: 0;
            margin-bottom: 6px;
        `,
    },
    qtyGridItem: { width: 3 },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 0;
            `,
        },
    },
    addToCartButtonGridItem: { width: 9 },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 13 },
            css: css`
                margin-bottom: 12px;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    // XNG-Changes
    showAvailability,
    showAttributes,
    actionsTemplate,
    variantSelectionCompleted,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    // const {
    //     showOverlayBadge,
    //     overlayBadge,
    //     overlayBadgeWrapperStyles,
    //     badgeAreaWrapperRef,
    //     overlayBadgeWrapperClickHandler,
    // } = useOverlayBadge({
    //     productContext,
    //     showTextBadge: showTextBadges,
    //     showImageBadge: showImageBadges,
    //     styles,
    // });

    // XNG-Changes
    const { showOverlayBadge, result, badgeAreaWrapperRef, overlayBadgeWrapperClickHandler } = xUseOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        notPositioned: true,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    /**
     * XNG-Changes
     * Update Structure
     */
    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridContainer extendedStyles={styles.productGridContainerStyle}>
                    {showImage && (
                        <ProductGridContainer.Image>
                            {/* XNG-Changes */}
                            {actionsTemplate ? actionsTemplate(productContext.product) : null}
                            <>
                                {!showOverlayBadge && (
                                    <ProductImage
                                        product={productContext}
                                        onClick={() => trackProductRecommendation(true)}
                                        extendedStyles={styles.productImageStyles}
                                    />
                                )}
                                {/* {showOverlayBadge && overlayBadge && (
                                    <StyledWrapper
                                        {...styles.badgeAreaWrapper}
                                        ref={badgeAreaWrapperRef}
                                        data-test-selector="productCarouselBadge"
                                    >
                                        <ProductImage
                                            extendedStyles={styles.productImageStyles}
                                            product={productContext}
                                        />
                                        <StyledWrapper
                                            {...overlayBadgeWrapperStyles}
                                            onClick={overlayBadgeWrapperClickHandler}
                                        >
                                            <Badge
                                                type={overlayBadge.badgeType}
                                                value={
                                                    overlayBadge.badgeType === "Image"
                                                        ? overlayBadge.largeImageBadgePath
                                                        : overlayBadge.displayText
                                                }
                                                badgeTextColor={overlayBadge.textColorHexCode}
                                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                                imageAltText={
                                                    overlayBadge.imageAltText
                                                        ? overlayBadge.imageAltText
                                                        : overlayBadge.name
                                                }
                                                textBadgeCss={styles.badgeText}
                                                imageBadgeProps={styles.badgeImageProps}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )} */}
                            </>
                        </ProductGridContainer.Image>
                    )}
                    <ProductGridContainer.Info>
                        {/* XNG-Changes: Display badges here instead of as overlays, 
                            since the product carousel shoudn't contain an image and 
                            they aren't technically overlaid on an image. */}
                        <StyledWrapper>
                            {showOverlayBadge && result.length && (
                                <StyledWrapper
                                    {...styles.badgeAreaWrapper}
                                    ref={badgeAreaWrapperRef}
                                    data-test-selector="productListBadge"
                                >
                                    {result.map(({ overlayBadge, overlayStyles }) => (
                                        <StyledWrapper
                                            key={overlayBadge.id}
                                            id={overlayBadge.id}
                                            {...mergeWithCss(overlayStyles, styles.badgeContainer)}
                                            onClick={overlayBadgeWrapperClickHandler}
                                        >
                                            <StyledWrapper key={overlayBadge.id} id={overlayBadge.id}>
                                                <Badge
                                                    type={overlayBadge.badgeType}
                                                    key={overlayBadge.id}
                                                    value={
                                                        overlayBadge.badgeType === "Image"
                                                            ? overlayBadge.largeImageBadgePath
                                                            : overlayBadge.displayText
                                                    }
                                                    badgeTextColor={overlayBadge.textColorHexCode}
                                                    badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                                    badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                                    imageAltText={
                                                        overlayBadge.imageAltText
                                                            ? overlayBadge.imageAltText
                                                            : overlayBadge.name
                                                    }
                                                    textBadgeCss={{
                                                        ...sharedBadgeStyle.badgeText,
                                                    }}
                                                    imageBadgeProps={styles.badgeImageProps}
                                                    // custom style for custom badges
                                                    {...{
                                                        textBadgeWrapperCss: {
                                                            ...mergeWithCss(sharedBadgeStyle.badge, {
                                                                css: css`
                                                                    width: max-content;
                                                                    max-width: 100%;
                                                                `,
                                                            }),
                                                        },
                                                    }}
                                                />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            )}

                            {/* XNG-Changes : also display custom badge */}
                            <CustomProductBadge
                                extendedStyles={styles.customProductBadge}
                                product={productContext.product}
                            />
                        </StyledWrapper>

                        {showTitle && (
                            <ProductDescription
                                product={productContext}
                                onClick={() => trackProductRecommendation(true)}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        )}
                        {showBrand && product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                showCustomerName={false}
                                showManufacturerItem={false}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        )}
                        {showAttributes && (
                            <ProductAttributes maximumNumberAttributeTypes={8} extendedStyles={styles.attributes} />
                        )}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                        {showImageBadges && imageBadges && (
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                        )}
                    </ProductGridContainer.Info>
                    <ProductGridContainer.Price>
                        {showPrice && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice
                                    showLabel={false}
                                    {...{ includeBreakPriceRange: true }}
                                    product={productContext}
                                />
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.productQuantityBreakPricingStyles}
                                />
                            </StyledWrapper>
                        )}
                    </ProductGridContainer.Price>
                    <ProductGridContainer.Availability>
                        {showAvailability && <ProductLeadTime />}
                    </ProductGridContainer.Availability>
                    <ProductGridContainer.AddToCartActions>
                        {showAddToCart && (
                            <>
                                <ProductQuoteButton />
                                <ProductBuyButton />
                            </>
                        )}
                    </ProductGridContainer.AddToCartActions>
                    <ProductGridContainer.AddToList>
                        {showAddToList && (
                            <ProductAddToListLink
                                data-test-selector="addToListLink"
                                extendedStyles={styles.productAddToListLinkStyles}
                            />
                        )}
                        {showAddToList && !variantSelectionCompleted && <StyledWrapper {...styles.addToList} />}
                    </ProductGridContainer.AddToList>
                </ProductGridContainer>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
